import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Paper,
  Button,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import EqualizerIcon from '@mui/icons-material/Equalizer';
import PersonIcon from '@mui/icons-material/Person';
import FolderIcon from '@mui/icons-material/Folder';
import FileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';

import ActivityTimeline from '../components/activity/ActivityTimeline';
import ActivityStats from '../components/activity/ActivityStats';
import PageHeader from '../components/ui/PageHeader';
import usePageTitle from '../hooks/usePageTitle';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`activity-tabpanel-${index}`}
    aria-labelledby={`activity-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

const a11yProps = (index: number) => ({
  id: `activity-tab-${index}`,
  'aria-controls': `activity-tabpanel-${index}`,
});

const ActivityDashboard: React.FC = () => {
  const { userId, resourceId, resourceType } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  const isScoped = Boolean(userId || (resourceId && resourceType));

  const pageTitle = useMemo(() => {
    if (userId) return 'User activity';
    if (resourceId && resourceType) {
      return resourceType === 'file' ? 'File activity' : 'Folder activity';
    }
    return 'Activity';
  }, [userId, resourceId, resourceType]);

  usePageTitle(pageTitle);

  const pageIcon = useMemo(() => {
    if (userId) return <PersonIcon fontSize="large" color="primary" />;
    if (resourceType === 'file') return <FileIcon fontSize="large" color="primary" />;
    if (resourceType === 'folder') return <FolderIcon fontSize="large" color="primary" />;
    return <TimelineIcon fontSize="large" color="primary" />;
  }, [userId, resourceType]);

  const description = useMemo(() => {
    if (userId) return `Activity history for user ${userId}`;
    if (resourceId && resourceType) {
      return `Recent events for this ${resourceType}`;
    }
    return 'Timeline and statistics for file and folder activity';
  }, [userId, resourceId, resourceType]);

  return (
    <Box sx={{ p: 3, maxWidth: '100%' }}>
      {isScoped && (
        <Breadcrumbs sx={{ mb: 2 }} aria-label="activity breadcrumb">
          <MuiLink
            component={RouterLink}
            to="/activity"
            underline="hover"
            color="inherit"
            variant="body2"
          >
            Activity
          </MuiLink>
          <Typography variant="body2" color="text.primary">
            {pageTitle}
          </Typography>
        </Breadcrumbs>
      )}

      <PageHeader
        title={pageTitle}
        icon={pageIcon}
        description={description}
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {isScoped && (
              <Button
                size="small"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/activity')}
              >
                All activity
              </Button>
            )}
            {userId && <Chip size="small" label={`User · ${userId.slice(0, 8)}…`} variant="outlined" />}
            {resourceId && resourceType && (
              <Chip
                size="small"
                label={`${resourceType} · ${resourceId.slice(0, 8)}…`}
                variant="outlined"
              />
            )}
          </Box>
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, next) => setTabValue(next)}
          aria-label="activity views"
          sx={{
            px: 1,
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 48,
            '& .MuiTab-root': { minHeight: 48, textTransform: 'none' },
          }}
        >
          <Tab icon={<TimelineIcon fontSize="small" />} iconPosition="start" label="Timeline" {...a11yProps(0)} />
          <Tab icon={<EqualizerIcon fontSize="small" />} iconPosition="start" label="Statistics" {...a11yProps(1)} />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
          <TabPanel value={tabValue} index={0}>
            <ActivityTimeline
              userId={userId}
              resourceId={resourceId}
              resourceType={resourceType as 'file' | 'folder' | undefined}
              maxItems={25}
              showFilters
              height={560}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ActivityStats userId={userId} height={560} />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
};

export default ActivityDashboard;
